import { ListButton } from "@refinedev/antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import {
  Button,
  Card,
  Collapse,
  ConfigProvider,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Skeleton,
  Spin,
  Space,
  Switch,
  TreeSelect,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ApiOutlined,
  LeftOutlined,
  CopyOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  ShopOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useGetIdentity, useWarnAboutChange } from "@refinedev/core";
import { useGuardarAlSalir } from "@/components/unsaved-changes";
import { axiosInstance } from "@/utilities";
import {
  API_BASE_URL,
  API_URL_PIM_ATTRIBUTES,
  API_URL_PIM_ATTRIBUTE_SETS,
  API_URL_PIM_DRAFTS,
  API_URL_PIM_PRODUCTS,
} from "@/services/XCONS_ENV";
import {
  ATRIBUTOS_DE_SISTEMA,
  NO_VIAJAN_EN_EL_GUARDADO,
} from "./system-attributes";
import {
  SKU_FABRICANTE_MAX,
  componerSkuXcons,
  skuFabricanteDe,
} from "./sku-xcons";
import {
  CONJUNTOS_DEL_ALTA,
  TITULO_DEL_ALTA,
  etiquetaConjunto,
} from "./conjuntos";
import { MiniaturaMedia } from "./MiniaturaMedia";
import { ProductStateSteps } from "./ProductStateSteps";
import { CabeceraFija } from "./CabeceraFija";
import { GaleriaOrdenable } from "./GaleriaOrdenable";
import { ZonaDeImagenes } from "./ZonaDeImagenes";
import { FilaQuitada } from "./GaleriaOrdenable";
import { SelectorDeOpcion } from "./SelectorDeOpcion";
import { cuerpoDelValor } from "./CampoDeValor";
import { HistorialDelProducto } from "./HistorialDelProducto";
import { PermisosDelProducto } from "./PermisosDelProducto";
import { DuplicarProducto } from "./DuplicarProducto";
import { ModalDeRechazo } from "./ModalDeRechazo";
import { aplicarClick, intermedias } from "./cascada-de-categorias";
import {
  type DestinoDelAsociador,
  type PrecargaDelSeller,
  valoresConTipoDeOpcion,
  valoresPrecargados,
} from "./valores-de-opciones";
import { storesConCategorias } from "./stores-con-categorias";
import type { AccionDeEstado } from "./ProductStateSteps";
import {
  AdjuntosEditables,
  esLinkSeguro,
  SIN_VINCULOS,
  SelectorDeProductos,
  VINCULOS,
} from "./VinculosYAdjuntos";
import type {
  CatalogAttribute,
  CatalogDetail,
  CatalogGroup,
  CategoryNode,
  LinkKind,
  PendingDetail,
  ProductAttachment,
  ProductLink,
  ProductState,
  ProductStateView,
  ProductVideo,
  StoreCatalogTree,
} from "./types";

const { Text, Title } = Typography;

/**
 * La grilla de los campos: **360px cada uno, uno al lado del otro, tantos como entren**.
 * Con `auto-fill` las columnas las decide el ancho de la pantalla —cuatro o cinco en una
 * grande, una en una chica— sin que el campo cambie de ancho.
 */
const GRILLA: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, 360px)",
  columnGap: 24,
  rowGap: 16,
};

/**
 * El encabezado de sección mide 40px: 9px arriba y abajo más los 22px de la línea. El
 * padding de antd por default lo dejaba en 46.
 */
const TEMA_SECCION = { components: { Collapse: { headerPadding: "9px 16px" } } };

/** El espacio entre filas lo pone la grilla, no el margen de cada `Form.Item`. */
const CELDA: React.CSSProperties = { marginBottom: 0 };

/** Vacío para la compuerta: un `false` de un switch es un valor, un `[]` no. */
const vacio = (v: unknown) =>
  v === undefined ||
  v === null ||
  (typeof v === "string" && !v.trim()) ||
  (Array.isArray(v) && !v.length);

/** Un error de la compuerta CON-P001: el `attribute_code` que falla y por qué. */
type Hallazgo = { field: string; message: string };

/** Cómo puede nombrar la compuerta a la categoría, que no es un campo del form. */
const CAMPOS_DE_CATEGORIA = new Set(["category", "categories", "category_ids"]);

/** Los campos que el form NO ofrece, y cada uno por una razón distinta. */
/**
 * Los `frontend_input` de media de Magento, que **no son campos de carga**.
 *
 * El conjunto trae un grupo "Images" con seis: `image`, `small_image`, `thumbnail` y
 * `swatch_image` (`media_image`), más `media_gallery` y `gallery` (`gallery`). El form los
 * pintaba como inputs de texto con el path adentro, y eso es dos problemas a la vez:
 *
 * 1. **Son segunda fuente de verdad.** Los tres primeros son exactamente lo que el bloque
 *    de Imágenes gobierna con sus roles. Tipear un path ahí escribe el atributo directo,
 *    salteando la galería: el producto queda con un rol apuntando a un archivo que no
 *    está en `catalog_product_entity_media_gallery`. Es el mismo camino paralelo por el
 *    que se sacaron `vv_state` y `category_ids`.
 * 2. **`media_gallery` y `gallery` ni siquiera tienen un valor de texto**: su backend es
 *    la tabla de galería, no `catalog_product_entity_varchar`. Lo que se escriba ahí es
 *    basura en una columna que nadie lee.
 *
 * Se filtra por el tipo de input y no por una lista de códigos, así que un atributo de
 * media nuevo queda afuera solo.
 */
const INPUTS_DE_MEDIA = new Set(["media_image", "gallery"]);

/**
 * Los que el form no edita. Es la misma lista que esconde la ficha —vive en
 * `system-attributes.ts` con el porqué de cada uno—: si las dos pantallas no
 * coincidieran, el operador vería dos versiones del mismo producto.
 */
const NO_EDITABLES = ATRIBUTOS_DE_SISTEMA;

/** El orden de CON-P001 para componer el nombre. */
const ORDEN_NOMBRE = ["vv_type", "manufacturer", "vv_line", "vv_measure"];

/**
 * Form de producto: crear, editar y duplicar (PIM Fase 8).
 *
 * **UX6 se resuelve como pantalla larga con secciones colapsables**, que es la opción que el
 * propio design señala como "la que menos código pide" y la que ya usa el detalle read-only
 * de la Fase 3. Las secciones **no están escritas acá**: salen de los `eav_attribute_group`
 * del conjunto, así que un atributo nuevo en el conjunto aparece solo y en su lugar.
 *
 * **Tres cosas que el form no tiene, y ninguna es un olvido:**
 * - **Estado**: lo mueve la máquina de estados, con sus gates y su validación.
 * - **`visibility`**: es salida del predicado de oferta, no carga de nadie.
 * - **Botón de publicar**: guardar no publica. Sobre un producto que se está vendiendo, lo
 *   que se guarda queda como **versión pendiente** hasta que un Admin la apruebe — y el
 *   banner de arriba lo dice antes de que el operador escriba una letra.
 */
export const ProductFormPage: React.FC = () => {
  /**
   * Tres modos, y el tercero no es un lujo: un **borrador** no existe en Magento, así que no
   * se puede cargar por SKU —`GET /pim/products/:sku` da 404— y su única fuente es el payload
   * de la versión pendiente. Sin este modo **no había forma de editar un borrador desde la
   * UI**: el listado llevaba a la revisión y la revisión no tenía por dónde editar.
   */
  const { sku = "", id: pendingId } = useParams<{ sku?: string; id?: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const esBorrador = !!pendingId;
  const esAlta = !esBorrador && sku === "nuevo";
  const conjuntoInicial = Number(params.get("set") ?? 4);
  /** Los datos del producto del seller, si el alta viene de + Catalogar (xcons-docs#609). */
  const [precarga] = useState(
    () => (location.state as { precarga?: PrecargaDelSeller } | null)?.precarga,
  );
  /**
   * A dónde vuelve la ficha si se abrió desde el asociador (xcons-docs#609): al producto del
   * seller que se estaba asociando, con su cola. Viaja en cada navegación de la ficha —de
   * `nuevo` a su borrador— para no perderse en el camino.
   */
  const [volverAlAsociador] = useState(
    () =>
      (location.state as { volverAlAsociador?: DestinoDelAsociador } | null)
        ?.volverAlAsociador,
  );
  const conVuelta = volverAlAsociador ? { volverAlAsociador } : undefined;
  /** La salida de la ficha: el asociador si vino de ahí, si no el listado del Catálogo. */
  const salir = () =>
    volverAlAsociador
      ? navigate(volverAlAsociador.ruta, { state: volverAlAsociador.state })
      : navigate("/catalog");

  const [detalle, setDetalle] = useState<CatalogDetail | null>(null);
  const [grupos, setGrupos] = useState<CatalogGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [pendiente, setPendiente] = useState<{
    id: number;
    state: "draft" | "review";
  } | null>(null);
  /** El estado de un producto publicado: es lo que muestra el flujo de la cabecera. */
  const [estadoPublicado, setEstadoPublicado] = useState<ProductState | null>(
    null,
  );
  /** El estado del borrador que se está editando: sólo en `draft` se puede seguir borrador. */
  const [estadoBorrador, setEstadoBorrador] = useState<PendingDetail["state"] | null>(
    null,
  );
  const [saliendo, setSaliendo] = useState(false);
  /** El borrador que se edita: de acá salen sus fechas y su producto, si ya tiene uno. */
  const [borrador, setBorrador] = useState<PendingDetail | null>(null);
  /** Al editar, el título es el nombre **guardado**: no cambia mientras se tipea. */
  const [titulo, setTitulo] = useState("");
  /**
   * **Hay algo sin guardar.** Lo marca el form al cambiar un valor y cada bloque que vive
   * fuera del form —categorías, galería, vínculos, adjuntos— cuando el operador lo toca.
   * Decide el popup de la ← y el aviso de refine al navegar desde el menú.
   */
  const [sucio, setSucio] = useState(false);
  const tocar = () => setSucio(true);
  const { setWarnWhen } = useWarnAboutChange();
  useEffect(() => {
    setWarnWhen(sucio);
  }, [sucio, setWarnWhen]);
  useEffect(() => () => setWarnWhen(false), [setWarnWhen]);
  const [media, setMedia] = useState<
    Array<{
      file: string;
      /**
       * `image` o `external-video`. **Viaja en el guardado**: el write path cae a `image`
       * cuando falta, y guardar un producto con video lo convertía en imagen.
       */
      mediaType?: string;
      /** Los datos del video: viajan en el guardado y el write path escribe su `_value_video`. */
      video?: ProductVideo | null;
      /** La URL pública que arma el API. Vacía si el ambiente no tiene la base de media. */
      url?: string;
      label?: string | null;
      position?: number;
      roles?: string[];
      /**
       * Lo que el upload leyó del header. **Viaja en el payload** porque las cuatro
       * reglas de la imagen base de CON-P001 —800×800, JPG, 72 dpi y el nombre— se
       * validan contra esto: el write path recibe un path, no los bytes. Sin esto las
       * cuatro se salteaban solas y un PNG de captura daba 100%.
       */
      originalFilename?: string;
      format?: string;
      width?: number;
      height?: number;
      dpi?: number;
    }>
  >([]);
  /**
   * La galería con la que se abrió un producto que ya está a la venta, o su versión de
   * edición: contra ella se ve qué imágenes se sacan (xcons-docs#499). Vacía en un alta.
   */
  const [mediaInicial, setMediaInicial] = useState<typeof media>([]);
  const quitadas = useMemo(
    () =>
      mediaInicial.filter(
        (m) =>
          m.mediaType !== "external-video" && !media.some((x) => x.file === m.file),
      ),
    [mediaInicial, media],
  );
  const [linkVideo, setLinkVideo] = useState("");
  const [agregandoVideo, setAgregandoVideo] = useState(false);

  /** Los tres roles de Magento que juntos hacen "la imagen base": PDP, listado y miniatura. */
  const ROLES_BASE = ["image", "small_image", "thumbnail"];
  const esVideo = (m: { mediaType?: string }) => m.mediaType === "external-video";

  /**
   * **La base es la primera imagen**, no una que se elige (xcons-docs#479): se le dan los
   * roles a esa y se le sacan a las demás. Un video no puede serlo —su `file` es la
   * miniatura de YouTube—, así que es la primera que no es video. Sin base el producto no
   * pasa CON-P001 y el PDP queda sin foto.
   */
  const conBaseEnLaPrimera = <M extends { mediaType?: string; roles?: string[] }>(
    lista: M[],
  ): M[] => {
    const primera = lista.findIndex((m) => !esVideo(m));
    return lista.map((m, i) => ({
      ...m,
      roles:
        i === primera
          ? ROLES_BASE
          : (m.roles ?? []).filter((r) => !ROLES_BASE.includes(r)),
    }));
  };

  /** Mueve un item de la galería: la posición que se guarda es el orden en pantalla. */
  const moverMedia = (desde: number, hacia: number) => {
    tocar();
    setMedia((prev) => {
      if (hacia < 0 || hacia >= prev.length) return prev;
      const lista = [...prev];
      const [item] = lista.splice(desde, 1);
      lista.splice(hacia, 0, item);
      return conBaseEnLaPrimera(lista);
    });
  };

  const quitarMedia = (index: number) => {
    tocar();
    setMedia((prev) => conBaseEnLaPrimera(prev.filter((_, i) => i !== index)));
  };

  /** Un video de YouTube: el API baja miniatura y título; se asocia al guardar. */
  const agregarVideo = async () => {
    setAgregandoVideo(true);
    try {
      const { data } = await axiosInstance.post<{
        file: string;
        mediaType: string;
        video: ProductVideo;
      }>(`${API_URL_PIM_PRODUCTS}/media/video`, { url: linkVideo });
      setMedia((prev) => conBaseEnLaPrimera([...prev, { ...data, roles: [] }]));
      tocar();
      setLinkVideo("");
      message.success(`"${data.video.title}" agregado. Se asocia al guardar.`);
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo agregar el video",
      );
    } finally {
      setAgregandoVideo(false);
    }
  };

  /**
   * Las categorías **no son atributos del EAV**: viven en `catalog_category_product`, así
   * que no salen del conjunto y necesitan su propio bloque. Son obligatorias en la
   * compuerta. El stock ya no está acá: es del seller, no del producto.
   */
  const [categorias, setCategorias] = useState<number[]>([]);
  /**
   * `id → nombre` de las categorías que el producto **ya tiene**, resuelto por el API.
   *
   * El árbol que se muestra es el de UNA tienda, y un producto puede estar en categorías
   * de otras: sin esto, el tag de una categoría de otro árbol salía como `#148`. Es la
   * misma solución que `Marca`, que mostraba `8494`.
   */
  const [nombresCategoria, setNombresCategoria] = useState<
    Record<number, string>
  >({});

  /**
   * Related, cross-sell y up-sell, y las fichas técnicas. Como las categorías, **arrancan
   * con lo que el producto tiene** y se mandan siempre: vacío es una decisión del operador,
   * no el estado inicial.
   */
  const [vinculos, setVinculos] =
    useState<Record<LinkKind, ProductLink[]>>(SIN_VINCULOS);
  const [adjuntos, setAdjuntos] = useState<ProductAttachment[]>([]);

  const [duplicando, setDuplicando] = useState(false);
  const [rechazando, setRechazando] = useState(false);
  /**
   * Se intentó enviar a revisión con obligatorios sin cargar (xcons-docs#442): desde ahí
   * lo que falta se marca en rojo, también las categorías, que no son un campo del form.
   */
  const [marcarFaltantes, setMarcarFaltantes] = useState(false);
  /** Las secciones abiertas. `null` hasta que se toca una: arrancan todas. */
  const [abiertas, setAbiertas] = useState<string[] | null>(null);
  /** Decide el título del alta y el SKU XCONS: un servicio no pide peso ni envío. */
  const [nombreConjunto, setNombreConjunto] = useState<string | null>(null);

  /**
   * **SKU XCONS = `MARCA-SKU Fabricante`**, para los dos conjuntos del Excel de Maxi. En el
   * alta se tipea el del fabricante y el SKU se arma solo con la etiqueta de la marca; lo
   * arma de nuevo el API, que es el que decide. **Se fija al primer guardado**: el SKU es
   * la clave del borrador y de todo lo que después apunte al producto, así que después
   * quedan de lectura él, el SKU Fabricante y la Marca que lleva adentro. Para corregirlos
   * se duplica.
   */
  const usaSkuXcons =
    !!nombreConjunto && CONJUNTOS_DEL_ALTA.includes(nombreConjunto);
  const armaSku = esAlta && usaSkuXcons;
  const marcaId = Form.useWatch("manufacturer", form);
  const skuFabricanteTipeado = Form.useWatch("skuFabricante", form) as
    | string
    | undefined;
  const skuDelForm = Form.useWatch("sku", form) as string | undefined;
  const etiquetaMarca = useMemo(
    () =>
      grupos
        .flatMap((g) => g.attributes)
        .find((a) => a.code === "manufacturer")
        ?.options?.find((o) => String(o.value) === String(marcaId))?.label ??
      null,
    [grupos, marcaId],
  );
  /** El del fabricante que el SKU guardado lleva adentro; `null` en los 10 cruzados. */
  const skuFabricanteGuardado = armaSku
    ? null
    : skuFabricanteDe(skuDelForm, etiquetaMarca);
  const marcaFija = !esAlta && skuFabricanteGuardado !== null;

  useEffect(() => {
    if (!armaSku) return;
    form.setFieldValue(
      "sku",
      etiquetaMarca && skuFabricanteTipeado?.trim()
        ? componerSkuXcons(etiquetaMarca, skuFabricanteTipeado)
        : undefined,
    );
  }, [armaSku, etiquetaMarca, skuFabricanteTipeado, form]);

  /**
   * El esquema del conjunto —con las opciones de los select— se pide **por conjunto y no por
   * producto**: son 285 KB de las 8.162 opciones, y uno solo cubre 44.7k productos.
   */
  const cargarEsquema = useCallback(async (attributeSetId: number) => {
    const { data } = await axiosInstance.get<{
      name?: string;
      groups: CatalogGroup[];
    }>(`${API_URL_PIM_ATTRIBUTE_SETS}/${attributeSetId}`);
    setGrupos(data.groups ?? []);
    setNombreConjunto(data.name ?? null);
    return data.groups ?? [];
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      if (esBorrador) {
        const { data } = await axiosInstance.get<PendingDetail>(
          `${API_URL_PIM_DRAFTS}/${encodeURIComponent(pendingId!)}`,
        );
        const carga = data.payload ?? {};
        setEstadoBorrador(data.state);
        setBorrador(data);
        setTitulo(
          String(carga.values?.name ?? data.name ?? "").trim() || data.sku,
        );
        const setId = Number(carga.attributeSetId ?? 4);
        const gruposDelConjunto = await cargarEsquema(setId);
        form.setFieldsValue({
          sku: data.sku,
          attributeSetId: setId,
          // Lo importado trae las opciones como texto y el select las tiene como número
          // (xcons-docs#474): sin esto mostraba el id en vez de la etiqueta.
          ...valoresConTipoDeOpcion(carga.values ?? {}, gruposDelConjunto),
        });
        setCategorias((carga.categories ?? []).map((c) => Number(c.categoryId)));
        setMedia(conBaseEnLaPrimera(carga.media ?? []));
        // En una versión de edición la base es lo publicado, no lo guardado en la versión: si
        // no, una imagen sacada dejaba de verse al guardar el borrador.
        setMediaInicial([]);
        if (data.productId) {
          void axiosInstance
            .get<CatalogDetail>(`${API_URL_PIM_PRODUCTS}/${encodeURIComponent(data.sku)}`)
            .then(({ data: publicado }) => setMediaInicial(publicado.media ?? []))
            .catch(() => setMediaInicial(carga.media ?? []));
        }
        // El payload de un borrador guarda los vínculos como los mandó el form —con SKU y
        // nombre para poder mostrarlos— pero el contrato también acepta el id pelado.
        setVinculos({
          ...SIN_VINCULOS,
          ...Object.fromEntries(
            Object.entries(carga.links ?? {}).map(([kind, lista]) => [
              kind,
              (lista ?? []).map((e) =>
                typeof e === "object" ? e : { productId: Number(e) },
              ),
            ]),
          ),
        });
        setAdjuntos(
          (carga.attachmentIds ?? []).map((attachmentId) => ({
            attachmentId: Number(attachmentId),
            name: null,
            type: null,
            size: 0,
            isActive: true,
            url: "",
          })),
        );
      } else if (esAlta) {
        // El conjunto llega elegido desde el botón Nuevo producto del listado y no se
        // cambia acá: define qué form es. Para cambiarlo se sale y se vuelve a crear.
        const gruposDelConjunto = await cargarEsquema(conjuntoInicial);
        form.setFieldsValue({
          attributeSetId: conjuntoInicial,
          ...valoresPrecargados(precarga, gruposDelConjunto),
        });
      } else {
        const { data } = await axiosInstance.get<CatalogDetail>(
          `${API_URL_PIM_PRODUCTS}/${encodeURIComponent(sku)}`,
        );
        setDetalle(data);
        setTitulo(String(data.values?.name ?? "").trim() || data.sku);
        await cargarEsquema(data.attributeSet.id);
        form.setFieldsValue({
          sku: data.sku,
          attributeSetId: data.attributeSet.id,
          ...data.values,
        });

        // Media y categorías **no son atributos del EAV**, así que no vienen en
        // `values` y hasta ahora el form abría un producto publicado con la galería
        // vacía, sin categorías y sin cantidad. No se perdía nada al guardar —el write
        // path no borra media y las otras dos sólo se mandan si las tocás— pero el panel
        // de Validar sacaba **bloqueantes falsos**: "Categoría es obligatorio" sobre un
        // producto con categoría. Y desde la ficha se podía agregar, nunca sacar.
        // La primera imagen es la principal (xcons-docs#479): lo que venía con la base en
        // otra se guarda con la base en la primera.
        setMedia(conBaseEnLaPrimera(data.media ?? []));
        setMediaInicial(data.media ?? []);
        setVinculos({ ...SIN_VINCULOS, ...(data.links ?? {}) });
        setAdjuntos(data.attachments ?? []);
        setCategorias((data.categories ?? []).map((c) => Number(c.categoryId)));
        setNombresCategoria(
          Object.fromEntries(
            (data.categories ?? [])
              .filter((c) => c.name)
              .map((c) => [Number(c.categoryId), c.name as string]),
          ),
        );
        // ¿Tiene una versión pendiente encima? Es lo que decide el banner.
        const { data: estado } = await axiosInstance.get<
          Pick<ProductStateView, "state" | "pending">
        >(`${API_URL_PIM_PRODUCTS}/${data.magentoProductId}/state`);
        setPendiente(estado.pending);
        setEstadoPublicado(estado.state);
      }
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo cargar el producto",
      );
    } finally {
      setLoading(false);
    }
  }, [esBorrador, pendingId, esAlta, sku, conjuntoInicial, cargarEsquema, form, precarga]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /**
   * **Se puede seguir borrador** en un alta y en un borrador que todavía no se mandó. Ahí
   * hay dos botones: Guardar borrador deja el producto como está, y Guardar lo manda a
   * revisión. En un producto en revisión o publicado hay uno solo, porque un publicado no
   * vuelve a borrador: saldría de la venta.
   */
  const sigueBorrador = esAlta || estadoBorrador === "draft";

  /**
   * **La versión de edición** (xcons-docs#432): lo que se edita sobre un producto que se
   * está vendiendo. Se abre desde Editar en la ficha —ruta por SKU de un activo— o se
   * retoma desde su pendiente, que tiene `productId`. Lo publicado se sigue vendiendo
   * hasta que se apruebe. **Un pausado también** (xcons-docs#482): ya no se edita directo, y
   * el cambio se aplica recién al aprobar la versión. Un archivado sigue editándose directo.
   */
  const esVersionDeEdicion =
    (!esAlta &&
      !esBorrador &&
      (estadoPublicado === "active" || estadoPublicado === "inactive")) ||
    (esBorrador && !!borrador?.productId);

  /**
   * La versión de edición recién abierta sobre un activo, sin pendiente todavía. Está en
   * Borrador y su paso siguiente es Enviar a revisión, como en un alta (xcons-docs#458):
   * sin el botón, la única forma de mandarla era salir y elegir Guardar y salir.
   */
  const versionSinGuardar = esVersionDeEdicion && !esBorrador && !pendiente;

  /**
   * **Dónde Guardar borrador deja todo como borrador** (xcons-docs#482): un alta, un borrador
   * sin mandar y la versión de edición recién abierta sobre un activo o un pausado. Ahí el
   * guardado va con `asDraft`, así que ni entra a la cola ni toca lo publicado.
   */
  const guardaComoBorrador = sigueBorrador || versionSinGuardar;

  /**
   * Lo que le falta para pasar a revisión: los obligatorios del conjunto, el nombre, el SKU
   * Fabricante del alta y al menos una categoría. **Es una pista, no la regla**: la compuerta
   * de verdad es CON-P001 en el backend, que igual corre al mandar a revisión.
   */
  const valores = Form.useWatch([], { form, preserve: true }) as
    | Record<string, unknown>
    | undefined;

  /**
   * Guarda y devuelve dónde quedó, o `null` si no se pudo.
   *
   * **Sólo el SKU frena el guardado**: es la clave del borrador y el backend lo rechaza sin
   * él. Los obligatorios del conjunto se marcan en rojo pero no frenan —un borrador acepta
   * cualquier contenido— y lo que falta de verdad lo exige la compuerta al pasar a revisión.
   */
  const persistir = async ({
    comoBorrador = false,
  }: {
    /**
     * Sobre un publicado, deja la versión de edición en borrador (`asDraft`): sin esto un
     * activo la deja en revisión y un pausado se escribe directo (xcons-docs#482).
     */
    comoBorrador?: boolean;
  } = {}): Promise<{
    mode: "draft" | "pending" | "direct";
    pendingId?: number;
    magentoProductId?: number;
  } | null> => {
    // Valida el SKU pero **los valores se leen del store completo**. `validateFields()`
    // devuelve sólo los campos MONTADOS, y las secciones colapsadas no montan sus inputs:
    // guardar con secciones cerradas perdía todo lo que hubiera adentro.
    // `getFieldsValue(true)` trae el store entero, incluidos los campos desmontados.
    try {
      await form.validateFields(armaSku ? ["sku", "skuFabricante"] : ["sku"]);
    } catch {
      return null;
    }
    const values = form.getFieldsValue(true);
    setGuardando(true);
    try {
      const { attributeSetId, sku: skuForm, ...resto } = values;

      // **Se manda lo que el form edita, no lo que precargó.** La precarga hace
      // `setFieldsValue({ ...data.values })` con los 84 atributos del detalle y
      // `NO_EDITABLES` sólo filtra el RENDERIZADO, así que el submit los devolvía todos.
      // Dos consecuencias, y la segunda es la grave: filas NULL de `visibility` y
      // compañía en atributos que el operador nunca vio, y sobre todo `vv_state`,
      // `status` y `url_key` viajando en el payload. Guardar un form que se abrió antes
      // de que otro operador pausara el producto lo devolvía a `active`: la venta se
      // reabría sin el gate de Admin, sin evento de historial y sin tocar los índices.
      //
      // **`name` sí viaja**, aunque esté escondido de las secciones: se edita arriba, en
      // el bloque de identidad. Filtrarlo por estar en la misma lista dejó de mandar el
      // nombre nuevo sin que nada fallara — el guardado salía bien, la versión pendiente
      // se creaba, y el renombrado no estaba en ninguna parte. De ahí las dos listas.
      //
      // **Y sólo lo que el form MUESTRA.** Para Simple Products y Services el API manda
      // únicamente los atributos del Excel de Maxi; el resto del conjunto (lo que escriben
      // los crons de Magento, el precio) viene igual en la precarga del detalle, y
      // devolverlo lo reescribiría con lo que se leyó al abrir. Como el guardado escribe
      // sólo lo que recibe, lo que no viaja queda intacto en Magento.
      const visibles = new Set([
        "name",
        ...secciones.flatMap((g) => g.attributes.map((a) => a.code)),
      ]);
      const editables = Object.fromEntries(
        Object.entries(resto).filter(
          ([code]) => visibles.has(code) && !NO_VIAJAN_EN_EL_GUARDADO.has(code),
        ),
      );

      const { data } = await axiosInstance.post<{
        mode: "draft" | "pending" | "direct";
        pendingId?: number;
        magentoProductId?: number;
      }>(API_URL_PIM_PRODUCTS, {
        sku: skuForm,
        ...(armaSku ? { skuFabricante: values.skuFabricante } : {}),
        // **Desde la página de una versión de edición `detalle` no se carga**: el producto
        // sale de la versión. Sin esto el guardado viajaba sin `productId`, el API lo tomaba
        // como un alta y lo rechazaba porque el SKU ya existe (xcons-docs#482).
        productId: detalle?.magentoProductId ?? borrador?.productId ?? undefined,
        attributeSetId,
        values: editables,
        // **Sin `url`.** Es un campo DERIVADO: el API lo arma al leer, con la base de
        // media del ambiente. Devolverlo sería guardar en el producto una URL que
        // depende de dónde se lo miró, y además el DTO no lo declara — el pipe corre con
        // `forbidNonWhitelisted`, así que el guardado entero se cae con un 400.
        //
        // La posición es **el orden en pantalla** (1-based, como la galería de Magento):
        // es lo que el operador reordenó. `video` sí viaja: el write path escribe su fila
        // de `_value_video`, que es lo que el PDP reproduce.
        media: media.map(({ url: _derivada, ...archivo }, i) => ({
          ...archivo,
          position: i + 1,
        })),
        // **La galería completa**: lo que se quitó acá se desasocia al guardar. Sin la
        // bandera el API suma (así la usa el importador) y quitar no hacía nada.
        replaceMedia: true,
        // **Se manda siempre**, y el cambio es consecuencia de la precarga. Mientras el
        // bloque arrancaba vacío había que distinguir `undefined` ("no toco las
        // categorías") de `[]` ("sacale todas"), porque vacío era el estado inicial y no
        // una decisión. Ahora arranca con lo que el producto tiene, así que vacío **es**
        // una decisión: el operador las sacó. El backend acota el borrado a los árboles
        // de sus tiendas, así que esto no puede tocar las de un store ajeno.
        categories: categorias.map((categoryId) => ({ categoryId })),
        // Los vínculos llevan SKU y nombre además del id: el write path usa sólo
        // `productId` y `position`, pero el payload de un borrador es lo único que lo
        // rehidrata, y sin eso reabrirlo mostraba `#45999`.
        links: Object.fromEntries(
          VINCULOS.map(({ kind }) => [
            kind,
            vinculos[kind].map(({ productId, sku, name }, position) => ({
              productId,
              sku,
              name,
              position,
            })),
          ]),
        ),
        attachmentIds: adjuntos.map((a) => a.attachmentId),
        ...(comoBorrador ? { asDraft: true } : {}),
      });
      // Se apaga ya y no en el efecto: casi todo guardado navega enseguida, y el aviso de
      // refine saltaría antes de que el efecto corra. **Y con `flushSync`**: sin él los dos
      // `set` quedan en cola hasta el próximo render, el `navigate` de quien llama corre
      // antes, y el bloqueo de refine todavía está armado — el guardado que deja una versión
      // pendiente navega a su vista y mostraba "Tienes cambios sin guardar" después de guardar.
      flushSync(() => {
        setSucio(false);
        setWarnWhen(false);
      });
      return data;
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo guardar",
      );
      return null;
    } finally {
      setGuardando(false);
    }
  };

  /**
   * Después del primer guardado el alta ya es un borrador: se sigue editando desde su
   * ruta. Quedarse en `nuevo` haría que el próximo guardado intente crearlo otra vez.
   * La versión de edición de un publicado, igual (xcons-docs#482): desde su ruta se edita
   * la versión, no lo publicado.
   */
  const seguirEnElBorrador = async (id?: number) => {
    if (!esBorrador && id)
      navigate(`/catalog/pending/${id}/edit`, { replace: true, state: conVuelta });
    else await cargar();
  };


  /**
   * Guardar ✓ sobre un borrador: guarda y lo **manda a revisión**. Son dos llamadas —el
   * guardado y el `submit`— porque la compuerta de CON-P001 vive en la transición, no en
   * el guardado. Si la compuerta frena, lo guardado queda como borrador y se dice qué falta.
   */
  const guardarYMandar = async () => {
    // **Como borrador y después el `submit`**, también en la versión de edición
    // (xcons-docs#482): sin `asDraft` un pausado se escribiría directo, y así los dos pasan
    // por la compuerta de CON-P001 igual que un alta.
    const data = await persistir({ comoBorrador: true });
    if (!data) return;
    const id = data.pendingId ?? (pendingId ? Number(pendingId) : undefined);
    if (!id) {
      message.error("Se guardó, pero no llegó el borrador para mandarlo a revisión");
      return;
    }
    setGuardando(true);
    try {
      await axiosInstance.post(
        `${API_URL_PIM_DRAFTS}/${encodeURIComponent(String(id))}/submit`,
      );
      message.success(
        esVersionDeEdicion ? "Cambios enviados a revisión" : "Producto enviado a revisión",
      );
      // **Se queda en el form** (xcons-docs#490), con el estado ya en revisión. Un alta pasa a
      // la ruta de su borrador; uno que ya estaba ahí se recarga.
      await seguirEnElBorrador(id);
    } catch (e) {
      const res = (
        e as {
          response?: {
            data?: {
              message?: string;
              code?: string;
              findings?: Array<{ field: string; message: string }>;
            };
          };
        }
      )?.response;
      if (res?.data?.code === "CON_P001_BLOCKING") {
        const faltan = res.data.findings?.length
          ? res.data.findings
          : [{ field: "", message: res.data.message ?? "Falta completar la ficha" }];
        // **Cada error en su campo** (Diseño, 2026-09-30). Un alta pasa a la ruta de su
        // borrador y el form se vuelve a montar: los errores viajan con la navegación y
        // se marcan al terminar de cargar. Lo demás ya está en pantalla: se marca acá.
        if (!esBorrador) {
          navigate(`/catalog/pending/${id}/edit`, {
            replace: true,
            state: { hallazgos: faltan, ...conVuelta },
          });
        } else {
          marcarHallazgos(faltan);
        }
        return;
      } else {
        message.error(
          res?.data?.message ?? "Se guardó como borrador, pero no se pudo mandar a revisión",
        );
      }
      await seguirEnElBorrador(id);
    } finally {
      setGuardando(false);
    }
  };

  /**
   * Guardar borrador (xcons-docs#479): guarda y se queda en el form. Un alta pasa a la ruta
   * de su borrador, para que el próximo guardado no intente crearlo otra vez.
   */
  const guardarBorrador = async () => {
    const data = await persistir({ comoBorrador: true });
    if (!data) return;
    message.success("Borrador guardado");
    await seguirEnElBorrador(
      data.pendingId ?? (pendingId ? Number(pendingId) : undefined),
    );
  };

  /**
   * Guardar ✓ sobre un producto en revisión o publicado. El mensaje dice **dónde quedó**,
   * que es lo único que le importa al operador: el mismo botón puede haber dejado una
   * versión pendiente o haber escrito en vivo, y confundirlos es creer que publicaste.
   */
  const guardarCambios = async () => {
    const data = await persistir();
    if (!data) return;
    if (data.mode === "pending") {
      message.warning(
        "Guardado como versión pendiente: no sale en vivo hasta que un Admin lo apruebe",
      );
    } else {
      message.success("Guardado");
    }
    if (data.mode === "pending" && data.pendingId) {
      navigate(`/catalog/pending/${data.pendingId}`);
    } else {
      await cargar();
    }
  };

  /** La ←: si hay algo sin guardar, pregunta antes de irse. */
  const volver = () => {
    if (sucio) setSaliendo(true);
    else salir();
  };

  const salirSinGuardar = () => {
    // Con `flushSync`, como al guardar y al rechazar: sin él el `navigate` corría antes de que
    // se apagara el aviso, y el popup global de cambios sin guardar interceptaba la salida y
    // preguntaba otra vez.
    flushSync(() => {
      setSaliendo(false);
      setSucio(false);
      setWarnWhen(false);
    });
    salir();
  };

  const salirGuardando = async () => {
    const data = await persistir({ comoBorrador: guardaComoBorrador });
    if (!data) {
      setSaliendo(false);
      return;
    }
    // La versión de edición guardada como borrador vuelve `pending`, pero no espera a nadie.
    message.success(
      data.mode === "draft" || (data.mode === "pending" && guardaComoBorrador)
        ? "Borrador guardado"
        : data.mode === "pending"
          ? "Guardado como versión pendiente: no sale en vivo hasta que un Admin lo apruebe"
          : "Guardado",
    );
    setSaliendo(false);
    salir();
  };

  /**
   * **Guardar también desde el popup del menú** (xcons-docs#490): salir por el menú o por un
   * link con cambios sin guardar ofrece el mismo guardado que la ←. Sale el popup, no la ficha:
   * la navegación la termina el popup.
   */
  useGuardarAlSalir(guardaComoBorrador ? "Guardar borrador" : "Guardar y salir", async () => {
    const data = await persistir({ comoBorrador: guardaComoBorrador });
    if (!data) return false;
    message.success(
      data.mode === "draft" || (data.mode === "pending" && guardaComoBorrador)
        ? "Borrador guardado"
        : data.mode === "pending"
          ? "Guardado como versión pendiente: no sale en vivo hasta que un Admin lo apruebe"
          : "Guardado",
    );
    return true;
  });

  const subirImagen = async (file: File) => {
    const body = new FormData();
    body.append("file", file);
    // **El SKU viaja con el archivo.** El backend renombra la imagen base a `SKU-01.jpg`
    // como pide CON-P001, y sin esto no tendría con qué: el endpoint de media sólo recibía
    // el archivo. Se lee del form y no de la ruta porque en un alta la ruta dice `nuevo`.
    const skuActual = String(form.getFieldValue("sku") ?? sku ?? "").trim();
    if (skuActual && skuActual !== "nuevo") body.append("sku", skuActual);
    try {
      const { data } = await axiosInstance.post<{
        file: string;
        deduped: boolean;
        originalFilename?: string;
        format?: string;
        width?: number;
        height?: number;
        dpi?: number;
        /** Lo que el normalizador avisa sin frenar: hoy, el fondo que no es blanco. */
        warnings?: string[];
      }>(
        `${API_URL_PIM_PRODUCTS}/media`,
        body,
        // **El header va explícito.** El `axiosInstance` trae
        // `Content-Type: application/json` como default de instancia, así que sin esto el
        // multipart nunca se parsea y el endpoint contesta "No llegó ningún archivo" con el
        // archivo ahí. Es el mismo patrón que usan los uploads de pagos y de logo de seller.
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      // La primera imagen es la base: es el rol que CON-P001 exige, y el que Magento usa en
      // el PDP. Sin esto "Imagen base" sigue faltando aunque haya fotos.
      setMedia((prev) =>
        conBaseEnLaPrimera([
          ...prev,
          {
            file: data.file,
            position: prev.length * 10,
            roles: [],
            originalFilename: data.originalFilename,
            format: data.format,
            width: data.width,
            height: data.height,
            dpi: data.dpi,
          },
        ]),
      );
      tocar();
      if (data.deduped) {
        // La deduplicación es por contenido: vale decirlo, porque el operador ve que no
        // aparece un archivo nuevo.
        message.info("Esa imagen ya estaba subida: se reusó el archivo");
      } else {
        // El backend la deja en 800×800 JPG y la renombra, pero el aviso no lo dice
        // (xcons-docs#479).
        message.success("Imagen cargada con éxito");
      }
      // Los `warnings` del upload —hoy, el fondo que no es blanco— no se muestran
      // (xcons-docs#479): no frenan nada, y una foto de contexto tampoco tiene fondo blanco.
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo subir la imagen",
      );
    }
    return false;
  };

  /** El asistente de nombre, con el orden que pide CON-P001. */
  const componerNombre = () => {
    const values = form.getFieldsValue();
    const partes = ORDEN_NOMBRE.map((code) => {
      const valor = values[code];
      if (valor === undefined || valor === null || valor === "") return null;
      const attr = grupos
        .flatMap((g) => g.attributes)
        .find((a) => a.code === code);
      const opcion = attr?.options?.find((o) => String(o.value) === String(valor));
      return opcion?.label ?? String(valor);
    }).filter(Boolean);

    if (!partes.length) {
      message.info(
        "Cargá tipo, marca, línea y medida y el nombre se arma con ese orden",
      );
      return;
    }
    form.setFieldValue("name", partes.join(" "));
    // `setFieldValue` no dispara `onValuesChange`: sin esto, componer no contaba como cambio.
    tocar();
  };

  /**
   * **Crear valor desde la ficha** (xcons-docs#490): lo crea en el atributo —queda para todos—
   * y lo suma a las opciones que ya se cargaron, para que aparezca sin pedir el esquema de
   * nuevo. Si el API no devuelve la opción creada, se pide el esquema y se la busca por nombre.
   */
  const crearOpcion = async (
    attribute: CatalogAttribute,
    label: string,
    imagen: File | null,
    color: string | null,
  ) => {
    type Creada = {
      optionId?: number;
      value?: number;
      id?: number;
      label?: string;
      image?: string | null;
      nuevo?: boolean;
    };
    try {
      const { body, config } = cuerpoDelValor(label, imagen, color);
      const { data } = await axiosInstance.post<Creada>(
        `${API_URL_PIM_ATTRIBUTES}/${encodeURIComponent(attribute.code)}/options`,
        body,
        config,
      );
      const id = Number(data?.optionId ?? data?.value ?? data?.id);
      /** La muestra elegida en el popup, por si el API no la devuelve (xcons-docs#499). */
      const muestraLocal = color ?? (imagen ? URL.createObjectURL(imagen) : null);
      let opcion:
        | { value: number; label: string; image?: string | null; nuevo?: boolean }
        | undefined =
        Number.isFinite(id)
          ? {
              value: id,
              label: data?.label ?? label,
              // Mientras la API no devuelva la muestra, la elegida en el popup: si no, el
              // valor recién creado aparecía sin su color ni su imagen (xcons-docs#499).
              image: data?.image || muestraLocal,
              // Creado desde la ficha: queda sin revisar hasta que se apruebe el producto.
              nuevo: data?.nuevo ?? true,
            }
          : undefined;
      if (opcion) {
        const nueva = opcion;
        setGrupos((prev) =>
          prev.map((g) => ({
            ...g,
            attributes: g.attributes.map((a) =>
              a.code === attribute.code &&
              !(a.options ?? []).some((o) => String(o.value) === String(nueva.value))
                ? { ...a, options: [...(a.options ?? []), nueva] }
                : a,
            ),
          })),
        );
      } else {
        const actualizados = await cargarEsquema(Number(form.getFieldValue("attributeSetId")));
        opcion = actualizados
          .flatMap((g) => g.attributes)
          .find((a) => a.code === attribute.code)
          ?.options?.find((o) => o.label.trim().toLowerCase() === label.toLowerCase());
        // Recargado del esquema tampoco trae la muestra si el API no la guarda todavía: se
        // le pone la elegida, en la opción y en el atributo, para que se vea en la lista.
        if (opcion && !opcion.image && muestraLocal) {
          const conMuestra = { ...opcion, image: muestraLocal };
          opcion = conMuestra;
          setGrupos((prev) =>
            prev.map((g) => ({
              ...g,
              attributes: g.attributes.map((a) =>
                a.code === attribute.code
                  ? {
                      ...a,
                      options: (a.options ?? []).map((o) =>
                        String(o.value) === String(conMuestra.value) ? conMuestra : o,
                      ),
                    }
                  : a,
              ),
            })),
          );
        }
      }
      if (!opcion) {
        message.error("Se creó el valor, pero no aparece en la lista: recargá la página");
        return null;
      }
      // Un `option_id` del EAV es número; el select de este atributo puede tenerlos como texto.
      const value =
        typeof attribute.options?.[0]?.value === "string" ? String(opcion.value) : opcion.value;
      message.success(`"${opcion.label}" creado: ya está disponible para todos`);
      return { ...opcion, value: value as number };
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo crear el valor",
      );
      return null;
    }
  };

  const campo = (attribute: CatalogAttribute) => {
    // Un atributo de opciones **se elige, no se tipea**, también si todavía no tiene ninguna:
    // ahí el selector arranca vacío y se carga con Crear valor (xcons-docs#490).
    if (
      attribute.options?.length ||
      attribute.frontendInput === "select" ||
      attribute.frontendInput === "multiselect"
    ) {
      const opciones = attribute.options ?? [];
      return (
        <SelectorDeOpcion
          attribute={attribute}
          onCrear={(label, imagen, color) => crearOpcion(attribute, label, imagen, color)}
          disabled={attribute.code === "manufacturer" && marcaFija}
          options={[
            ...opciones.map((o) => ({
              value: o.value,
              label: o.label,
            })),
            // Una opción **borrada** que el producto todavía tiene elegida: sin esto el
            // select mostraba el id suelto (`12957`) como si fuera el valor. Va
            // deshabilitada: se puede sacar, no volver a elegir.
            ...(detalle?.orphanOptions?.[attribute.code] ?? []).map((id) => ({
              value: typeof opciones[0]?.value === "number" ? id : String(id),
              label: `Opción borrada (#${id})`,
              disabled: true,
            })),
          ]}
        />
      );
    }
    switch (attribute.frontendInput) {
      case "boolean":
        return <Switch />;
      case "price":
      case "weight":
        return <InputNumber style={{ width: "100%" }} min={0} />;
      case "date":
        return <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />;
      case "textarea":
        // Mismo ancho que el resto, más alto, y se estira para abajo si hace falta.
        return <Input.TextArea rows={4} style={{ resize: "vertical" }} />;
      default:
        return <Input />;
    }
  };

  const secciones = useMemo(
    () =>
      grupos
        .map((grupo) => ({
          ...grupo,
          attributes: grupo.attributes.filter(
            (a) =>
              a.visible &&
              !NO_EDITABLES.has(a.code) &&
              !INPUTS_DE_MEDIA.has(a.frontendInput ?? ""),
          ),
        }))
        .filter((g) => g.attributes.length),
    [grupos],
  );

  const faltantes = useMemo(() => {
    const v = valores ?? {};
    return [
      ...(armaSku && vacio(v.skuFabricante) ? ["SKU Fabricante"] : []),
      ...(vacio(v.name) ? ["Nombre"] : []),
      ...secciones.flatMap((g) =>
        g.attributes
          .filter((a) => a.required && vacio(v[a.code]))
          .map((a) => a.label ?? a.code),
      ),
      ...(categorias.length ? [] : ["Categoría"]),
    ];
  }, [valores, armaSku, secciones, categorias]);

  /** Los campos en el orden en que se ven: decide a cuál error va la pantalla primero. */
  const ordenDeCampos = [
    ...(armaSku ? ["skuFabricante"] : []),
    "sku",
    "name",
    ...secciones.flatMap((g) => g.attributes.map((a) => a.code)),
  ];

  /** La pantalla va al primer campo con error, en el orden del form; si no, a Categorías. */
  const irAlPrimerError = (conError: string[], categoria: boolean) => {
    // Vuelve a abrir todas las secciones (xcons-docs#442): cerrada, el campo no se vería.
    // El scroll espera a que se abran.
    setAbiertas(null);
    const primero = ordenDeCampos.find((code) => conError.includes(code));
    setTimeout(() => {
      if (primero) {
        form.scrollToField(primero, { behavior: "smooth", block: "center" });
      } else if (categoria) {
        document
          .getElementById("bloque-categorias")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
  };

  /**
   * Lo que frenó la compuerta CON-P001, **cada error en su campo**: la regla viene por
   * `attribute_code`, y es el mismo `name` del Form.Item. Lo que no cae en un campo del
   * form —la galería, un vínculo— sigue en una lista aparte, para que no se pierda.
   */
  const marcarHallazgos = (hallazgos: Hallazgo[]) => {
    const enCampo = hallazgos.filter((h) => ordenDeCampos.includes(h.field));
    const deCategoria = hallazgos.some((h) => CAMPOS_DE_CATEGORIA.has(h.field));
    const sueltos = hallazgos.filter(
      (h) => !enCampo.includes(h) && !CAMPOS_DE_CATEGORIA.has(h.field),
    );
    form.setFields(
      enCampo.map((h) => ({
        name: h.field,
        errors: [
          ...enCampo.filter((x) => x.field === h.field).map((x) => x.message),
        ],
      })),
    );
    if (deCategoria) setMarcarFaltantes(true);
    message.error("Hay campos con errores: están marcados en rojo");
    irAlPrimerError(
      enCampo.map((h) => h.field),
      deCategoria,
    );
    if (sueltos.length) {
      Modal.info({
        title: "Se guardó como borrador, pero falta completar la ficha",
        width: 520,
        okText: "Entendido",
        content: (
          <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
            {sueltos.map((h) => (
              <li key={`${h.field}-${h.message}`}>{h.message}</li>
            ))}
          </ul>
        ),
      });
    }
  };

  /** Los errores de la compuerta que llegan con la navegación de un alta a su borrador. */
  const hallazgosAlLlegar = (location.state as { hallazgos?: Hallazgo[] } | null)
    ?.hallazgos;
  useEffect(() => {
    if (loading || !hallazgosAlLlegar) return;
    marcarHallazgos(hallazgosAlLlegar);
    // Se consumen: recargar la página no tiene que volver a marcarlos.
    navigate(location.pathname, { replace: true, state: conVuelta ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hallazgosAlLlegar]);

  // Al entrar desde la tabla, un spinner y no un esqueleto (xcons-docs#404).
  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  /** SKU Fabricante, SKU XCONS y Nombre: las primeras celdas de Datos principales. */
  const identidad = (
    <>
      {armaSku && (
        <Form.Item
          name="skuFabricante"
          label="SKU Fabricante"
          style={CELDA}
          validateTrigger={["onChange", "onBlur"]}
          rules={[
            { required: true, message: "El SKU Fabricante es obligatorio" },
            {
              validator: (_, v?: string) =>
                v && v !== v.trim()
                  ? Promise.reject(new Error("Sin espacios al inicio ni al final"))
                  : Promise.resolve(),
            },
          ]}
          extra="El del fabricante, sin la marca."
        >
          <Input maxLength={SKU_FABRICANTE_MAX} showCount />
        </Form.Item>
      )}
      {!armaSku && skuFabricanteGuardado && (
        <Form.Item label="SKU Fabricante" style={CELDA}>
          <Input value={skuFabricanteGuardado} disabled />
        </Form.Item>
      )}

      <Form.Item
        name="sku"
        label={usaSkuXcons ? "SKU XCONS" : "SKU"}
        style={CELDA}
        rules={[
          {
            required: true,
            message: armaSku
              ? "Elegí la Marca y cargá el SKU Fabricante: el SKU se arma con los dos"
              : "El SKU es obligatorio",
          },
        ]}
        extra={
          armaSku
            ? "Marca-SKU Fabricante. Es la clave del catálogo: se fija al guardar."
            : "Inmutable: es la clave con la que todo apunta a este producto."
        }
      >
        {/* **Se fija al primer guardado, también en un borrador.** El borrador se
            ancla en el SKU: guardarlo con otro no lo renombraba, creaba un segundo
            borrador y dejaba el primero huérfano. */}
        <Input
          disabled={!esAlta || armaSku}
          placeholder={armaSku ? "MARCA-SKU Fabricante" : undefined}
        />
      </Form.Item>

      <Form.Item
        name="name"
        label="Nombre"
        style={CELDA}
        // En rojo si se deja vacío, pero no frena el guardado: el nombre lo exige
        // CON-P001 al pasar a revisión. El botón Componer entra en los 360px.
        validateTrigger={["onChange", "onBlur"]}
        rules={[{ required: true, message: "El Nombre es obligatorio" }]}
      >
        <Input
          addonAfter={
            <Tooltip title="Arma el nombre con el orden de CON-P001: tipo, marca, línea y medida">
              <Button type="text" size="small" onClick={componerNombre}>
                Componer
              </Button>
            </Tooltip>
          }
        />
      </Form.Item>
    </>
  );


  /**
   * **Enviar a revisión con obligatorios vacíos no se bloquea: los señala** (Diseño,
   * 2026-09-30). El botón queda habilitado; al tocarlo, lo que falta se pone en rojo y la
   * pantalla va al primero, en el orden del form. La categoría no es un campo del form, así
   * que se marca con su propio aviso. La compuerta de verdad sigue siendo CON-P001.
   */
  const enviarARevision = async () => {
    // **Todas las reglas de cada campo**, no sólo la de obligatorio: lo que tenga una
    // restricción que no se cumple se pone en rojo con su motivo (Diseño, 2026-09-30).
    const conError: string[] = await form.validateFields().then(
      () => [],
      (e: { errorFields?: Array<{ name: Array<string | number> }> }) =>
        (e.errorFields ?? []).map((f) => String(f.name[0])),
    );
    const sinCategoria = !categorias.length;
    if (!conError.length && !sinCategoria)
      return void guardarYMandar();
    setMarcarFaltantes(true);
    message.error(
      faltantes.length
        ? "Campos obligatorios incompletos"
        : "Hay campos con errores: están marcados en rojo",
    );
    irAlPrimerError(conError, sinCategoria);
  };

  /** **Arrancan todas abiertas, en cualquier estado** (Diseño, 2026-09-30). */
  const seccionesDeEntrada = [
    ...secciones.map((g) => String(g.id)),
    "imagenes-y-videos",
    "adjuntos",
    "relacionados",
    "categorias",
  ];

  /**
   * Las secciones viven en dos `Collapse` —Categorías va aparte (xcons-docs#479)—: cada uno
   * cambia sólo las suyas y deja como estaban las del otro.
   */
  const alCambiarAbiertas = (propias: string[]) => (k: string | string[]) => {
    const abiertasAhora = Array.isArray(k) ? k : [k];
    setAbiertas((prev) => [
      ...(prev ?? seccionesDeEntrada).filter((key) => !propias.includes(key)),
      ...abiertasAhora,
    ]);
  };


  /**
   * El estado que muestra el flujo de la cabecera. Un alta todavía no existe, pero nace
   * borrador; un producto publicado trae el suyo del endpoint de estado.
   *
   * **La versión de edición muestra el estado de la versión, no el del producto**
   * (xcons-docs#432): lo que se está editando arranca en Borrador, aunque lo publicado se
   * siga vendiendo. Si ya tiene una pendiente, el de esa pendiente.
   */
  const estadoDelFlujo: ProductState | null = esAlta
    ? "draft"
    : esBorrador
      ? estadoBorrador
      : esVersionDeEdicion
        ? (pendiente?.state ?? "draft")
        : estadoPublicado;

  /**
   * **Aprobar desde el form** (Diseño, 2026-09-30): un alta en revisión se revisa editando.
   * Si quien revisa corrigió algo, se guarda antes: aprobar sin guardar publicaría lo de
   * antes de la corrección.
   */
  const aprobar = () =>
    Modal.confirm({
      title: "¿Querés aprobar este producto?",
      content:
        "Al aprobarlo, el producto pasa a formar parte del Catálogo y queda disponible para la venta.",
      okText: "Aprobar",
      cancelText: "Cancelar",
      onOk: async () => {
        if (sucio && !(await persistir())) return;
        setGuardando(true);
        try {
          await axiosInstance.post(
            `${API_URL_PIM_DRAFTS}/${encodeURIComponent(pendingId!)}/approve`,
          );
          message.success("Producto publicado");
          salir();
        } catch (e) {
          message.error(
            (e as { response?: { data?: { message?: string } } })?.response?.data
              ?.message ?? "No se pudo aprobar",
          );
        } finally {
          setGuardando(false);
        }
      },
    });

  /** Rechazar lo devuelve a su autor: lo que no se guardó acá no viaja. */
  const rechazar = async () => {
    setGuardando(true);
    try {
      await axiosInstance.post(
        `${API_URL_PIM_DRAFTS}/${encodeURIComponent(pendingId!)}/reject`,
      );
      message.success("Devuelta a edición");
      setRechazando(false);
      flushSync(() => {
        setSucio(false);
        setWarnWhen(false);
      });
      navigate("/catalog");
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo rechazar",
      );
    } finally {
      setGuardando(false);
    }
  };

  const enRevision = esBorrador && estadoBorrador === "review";
  // Aprobar y Rechazar sin permiso quedan deshabilitados, sin texto debajo (xcons-docs#479).

  /**
   * Las acciones van en el botón del flujo (xcons-docs#404), no al pie. En borrador el
   * botón **es** Enviar a revisión: guarda y manda en un paso. En revisión, Acciones con
   * Aprobar y Rechazar (Diseño, 2026-09-30). En la versión de edición de un activo, también
   * Enviar a revisión. Sobre un publicado no hay acciones acá: su estado se mueve desde la
   * ficha.
   */
  const accionesDelFlujo: AccionDeEstado[] = sigueBorrador || versionSinGuardar
    ? [
        {
          key: "review",
          label: "Enviar a revisión",
          onClick: () => void enviarARevision(),
          disabled: guardando,
        },
      ]
    : enRevision && borrador
      ? [
          {
            key: "approve",
            label: "Aprobar",
            onClick: aprobar,
            disabled: !borrador.permissions.canApprove || guardando,
          },
          {
            key: "reject",
            label: "Rechazar",
            onClick: () => setRechazando(true),
            disabled: !borrador.permissions.canReject || guardando,
          },
        ]
      : [];

  const faltaCategoria = marcarFaltantes && !categorias.length;

  /** El historial se ancla en el `entity_id` o, en un borrador que nunca salió, en el SKU. */
  const anclaDelHistorial =
    detalle?.magentoProductId ?? borrador?.productId ?? borrador?.sku;

  return (
    <>
      <Flex vertical gap={16}>
        {/* Nombre, botones y Estado del producto fijos al hacer scroll (xcons-docs#479). */}
        <CabeceraFija>
          {/* La cabecera de página del Admin, la misma de Canales: ← de 32×32 a 16px del
              título. En un alta el título dice qué se crea, porque el conjunto ya no se ve.
              Título y flujo alineados por arriba (xcons-docs#404). */}
          <Flex justify="space-between" align="flex-start" gap={16}>
            {/* `minWidth: 0` deja que el nombre se corte con "…": sin eso un nombre largo
                empuja el flujo a la línea de abajo. */}
            <Flex align="center" gap={8} style={{ minWidth: 0 }}>
              <Flex align="center" style={{ minWidth: 0 }}>
                <ListButton
                  hideText
                  icon={<LeftOutlined />}
                  onClick={volver}
                  style={{ marginRight: 16, flexShrink: 0 }}
                />
                <Title level={4} style={{ margin: 0 }} ellipsis={{ tooltip: true }}>
                  {esAlta
                    ? (TITULO_DEL_ALTA[nombreConjunto ?? ""] ?? "Nuevo producto")
                    : titulo}
                </Title>
              </Flex>
              {/* El tipo de producto junto al nombre, como en la ficha. En un alta no hace
                  falta: el título ya lo dice ("Nuevo producto simple"). */}
              {!esAlta && nombreConjunto && (
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                  {etiquetaConjunto(nombreConjunto)}
                </Tag>
              )}
            </Flex>
            {/* A la derecha, Guardar borrador o Guardar y Duplicar, junto a Permisos
                (xcons-docs#479). Permisos va en cualquier estado, como en la ficha: en un alta
                o un borrador nunca publicado el panel explica que todavía no hay dónde
                guardarlos. */}
            <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
              {/* Abierta desde + Catalogar del asociador (xcons-docs#609): la vuelta al
                  producto del seller. Asociarlo se puede cuando la ficha esté aprobada. */}
              {volverAlAsociador && (
                <Button icon={<ApiOutlined />} onClick={volver}>
                  Volver al asociador
                </Button>
              )}
              {/* **Guardar borrador** junto a Duplicar (xcons-docs#479), del tamaño de Enviar a
                  revisión: guarda sin salir. Donde se puede seguir borrador —un alta, un
                  borrador sin mandar y la versión de edición recién abierta sobre un activo o
                  un pausado (xcons-docs#482)—, y ahí deja todo en borrador. */}
              {guardaComoBorrador && (
                <Button loading={guardando} onClick={() => void guardarBorrador()}>
                  Guardar borrador
                </Button>
              )}
              {/* También "Guardar borrador" donde guarda directo, que desde xcons-docs#482 es
                  sólo un archivado (xcons-docs#479): el flujo que se nombra es siempre
                  borrador → revisión → aprobar. */}
              {!sigueBorrador && !esVersionDeEdicion && (
                <Button loading={guardando} onClick={() => void guardarCambios()}>
                  Guardar borrador
                </Button>
              )}
              {/* Duplicar, sólo el ícono. En un borrador es la salida cuando la marca está mal
                  (el SKU la lleva adentro). Un alta todavía no tiene qué duplicar, y en la
                  versión de edición se duplica desde la ficha del publicado. */}
              {!esAlta && !esVersionDeEdicion && (
                <Tooltip title="Duplicar">
                  <Button
                    aria-label="Duplicar"
                    icon={<CopyOutlined />}
                    onClick={() => setDuplicando(true)}
                  />
                </Tooltip>
              )}
              <PermisosDelProducto
                productId={detalle?.magentoProductId ?? borrador?.productId}
                autorUserId={borrador?.authorUserId}
              />
            </Flex>
          </Flex>

          {/* El flujo en su propia sección, a todo el ancho, con las fechas en el
              encabezado. En un alta todavía no hay fechas. */}
          {estadoDelFlujo && (
            <ProductStateSteps
              state={estadoDelFlujo}
              acciones={accionesDelFlujo}
              /* Lo que era la columna Contexto del listado (xcons-docs#432), junto al estado
                 (xcons-docs#442): un borrador que nunca salió, o la edición de uno que se
                 vende. */
              etiquetas={
                <>
                  {/* Sin "Nunca publicado" en el form (xcons-docs#479): en un producto nuevo
                      no dice nada que el título no diga. */}
                  {/* En la versión de edición, el acceso directo a lo que está a la venta
                      (xcons-docs#490), como en la vista de la revisión (xcons-docs#487). */}
                  {esVersionDeEdicion && (borrador?.sku ?? sku) && (
                    <Typography.Link
                      onClick={() =>
                        navigate(`/catalog/${encodeURIComponent(borrador?.sku ?? sku)}`)
                      }
                    >
                      Hay una versión activa de este producto
                    </Typography.Link>
                  )}
                </>
              }
              fechas={
                detalle
                  ? { creado: detalle.createdAt, editado: detalle.updatedAt }
                  : borrador
                    ? { creado: borrador.createdAt, editado: borrador.updatedAt }
                    : undefined
              }
              anchoCompleto
            />
          )}
        </CabeceraFija>

        {/* Sin banner de versión pendiente (xcons-docs#479): el flujo de estado ya dice
            que hay una versión de edición, y el aviso al guardar dice dónde quedó. */}

        {/* Sin Card alrededor: las secciones ya tienen su borde, y el recuadro de afuera
            dejaba un marco blanco de más. */}
        <Form
          form={form}
          layout="vertical"
          disabled={guardando}
          onValuesChange={tocar}
        >
          {/* El conjunto no se elige acá: llega desde Nuevo producto y viaja escondido. */}
          <Form.Item name="attributeSetId" hidden>
            <Input />
          </Form.Item>

          {/* Las secciones salen del conjunto, no de una lista escrita acá: un atributo
              nuevo aparece solo y en el grupo que le corresponde. La identidad —SKU
              Fabricante, SKU XCONS y Nombre— va al frente de la primera, que es Datos
              principales. Imágenes y videos va al final, en el mismo bloque: no es un grupo del
              conjunto, pero se carga igual que el resto de la ficha.
              **Arrancan todas abiertas, en cualquier estado** (Diseño, 2026-09-30). */}
          <ConfigProvider theme={TEMA_SECCION}>
            <Collapse
              activeKey={abiertas ?? seccionesDeEntrada}
              onChange={alCambiarAbiertas(seccionesDeEntrada.filter((k) => k !== "categorias"))}
              items={[
                ...secciones.map((grupo, i) => ({
                  key: String(grupo.id),
                  // Monta el panel aunque esté cerrado: si no, sus inputs no se registran en
                  // el form y el guardado no los ve. Es lo que hacía que un producto cargado
                  // con las secciones cerradas se guardara a medias.
                  forceRender: true,
                  label: <Text strong>{grupo.name}</Text>,
                  children: (
                    <div style={GRILLA}>
                      {i === 0 && identidad}
                      {grupo.attributes.map((attribute) => (
                        <Form.Item
                          key={attribute.code}
                          name={attribute.code}
                          label={attribute.label ?? attribute.code}
                          style={CELDA}
                          extra={
                            attribute.code === "manufacturer" && marcaFija
                              ? "Fija: el SKU la lleva adentro. Para cambiarla, duplicá el producto."
                              : undefined
                          }
                          valuePropName={
                            attribute.frontendInput === "boolean"
                              ? "checked"
                              : undefined
                          }
                          /**
                           * **Se marca en rojo, pero no frena el guardado.**
                           *
                           * Un borrador acepta cualquier contenido, incluso incompleto: lo
                           * obligatorio se exige recién al pasar a revisión, y eso lo decide
                           * CON-P001 en el backend. Por eso `persistir` valida sólo el SKU:
                           * la regla acá es para que el campo se ponga rojo cuando el
                           * operador lo toca y lo deja vacío, no un gate.
                           */
                          rules={
                            attribute.required
                              ? [
                                  {
                                    required: true,
                                    message: `${attribute.label ?? attribute.code} es obligatorio`,
                                  },
                                ]
                              : undefined
                          }
                          validateTrigger={["onChange", "onBlur"]}
                        >
                          {campo(attribute)}
                        </Form.Item>
                      ))}
                    </div>
                  ),
                })),
                {
                  key: "imagenes-y-videos",
                  forceRender: true,
                  label: <Text strong>Imágenes y videos</Text>,
                  children: (
                    <Flex vertical gap={12}>
                      <ZonaDeImagenes onArchivo={subirImagen} />

                      <Space.Compact style={{ width: "100%" }}>
                        <Input
                          prefix={<YoutubeOutlined />}
                          placeholder="Pegá un link de YouTube para agregar un video"
                          value={linkVideo}
                          onChange={(e) => setLinkVideo(e.target.value)}
                          onPressEnter={() => linkVideo.trim() && void agregarVideo()}
                        />
                        <Button
                          loading={agregandoVideo}
                          disabled={!linkVideo.trim()}
                          onClick={() => void agregarVideo()}
                        >
                          Agregar video
                        </Button>
                      </Space.Compact>

                      {/* Se ordena arrastrando, y la primera imagen es la principal: sin números,
                          estrella ni flechas (xcons-docs#479). Abajo, con el mismo formato, las que
                          se sacan de un producto que ya está a la venta (xcons-docs#499). */}
                      {media.length > 0 && (
                        <GaleriaOrdenable
                          items={media}
                          idDe={(m) => m.file}
                          onMover={moverMedia}
                          renderExtra={(item, index) => (
                            <Button
                              type="text"
                              size="small"
                              aria-label={esVideo(item) ? "Quitar el video" : "Quitar la imagen"}
                              icon={<DeleteOutlined style={{ fontSize: 12, color: "#000" }} />}
                              // Sin confirmar (xcons-docs#479): se saca del producto recién al
                              // guardar, y salir sin guardar la devuelve. El archivo de S3 no se
                              // borra: el barrido de huérfanos es de la Fase 13 y corre aparte.
                              onClick={() => quitarMedia(index)}
                            />
                          )}
                          renderItem={(item) => (
                            <Space>
                              <MiniaturaMedia file={item.file} url={item.url} />
                              <Space direction="vertical" size={0}>
                                {item.mediaType === "external-video" ? (
                                  <Space size={4}>
                                    <Tag color="purple">video</Tag>
                                    {esLinkSeguro(item.video?.url) ? (
                                      <a href={item.video!.url!} target="_blank" rel="noreferrer">
                                        {item.video.title || item.video.url}
                                      </a>
                                    ) : (
                                      <Text>{item.video?.title ?? "(sin link)"}</Text>
                                    )}
                                  </Space>
                                ) : (
                                  <Text code>{item.file}</Text>
                                )}
                                {item.roles?.includes("image") ? (
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    Principal
                                  </Text>
                                ) : null}
                              </Space>
                            </Space>
                          )}
                        />
                      )}
                      {quitadas.map((m) => (
                        <FilaQuitada key={m.file} file={m.file} url={m.url} />
                      ))}
                    </Flex>
                  ),
                },
                {
                  key: "adjuntos",
                  forceRender: true,
                  label: <Text strong>Adjuntos</Text>,
                  // **Sin buscador de fichas existentes** (xcons-docs#432, pedido del PM):
                  // se sube un archivo o se carga un link. Lo asociado va en tarjetas con
                  // vista previa y tacho, como las facturas de Pedidos (xcons-docs#490).
                  children: (
                    <Flex vertical gap={8}>
                      <AdjuntosEditables
                        adjuntos={adjuntos}
                        onCreado={(a) => {
                          setAdjuntos((prev) =>
                            prev.some((x) => x.attachmentId === a.attachmentId)
                              ? prev
                              : [...prev, a],
                          );
                          tocar();
                        }}
                        onQuitar={(a) => {
                          setAdjuntos((prev) =>
                            prev.filter((x) => x.attachmentId !== a.attachmentId),
                          );
                          tocar();
                        }}
                      />
                    </Flex>
                  ),
                },
                {
                  key: "relacionados",
                  forceRender: true,
                  label: <Text strong>Productos relacionados</Text>,
                  children: (
                    <Flex vertical gap={12}>
                      {VINCULOS.map(({ kind, label }) => (
                        <div key={kind}>
                          <Text strong>{label}</Text>
                          <SelectorDeProductos
                            value={vinculos[kind]}
                            excluirProductId={detalle?.magentoProductId}
                            onChange={(lista) => {
                              setVinculos((prev) => ({ ...prev, [kind]: lista }));
                              tocar();
                            }}
                          />
                        </div>
                      ))}
                    </Flex>
                  ),
                },
              ]}
            />
          </ConfigProvider>

          {/* Categorías va fuera del bloque de la ficha (xcons-docs#479), con el mismo
              encabezado. Comparte las secciones abiertas con el de arriba. */}
          <ConfigProvider theme={TEMA_SECCION}>
            <Collapse
              style={{ marginTop: 16 }}
              activeKey={abiertas ?? seccionesDeEntrada}
              onChange={alCambiarAbiertas(["categorias"])}
              items={[
                {
                  key: "categorias",
                  forceRender: true,
                  label: <Text strong>Categorías</Text>,
                  children: (
                    <>
                      {/* El bloque PRECARGA lo que el producto tiene: lo que se ve es el
                          estado real y sacar una categoría la saca de verdad. Sin aviso que lo
                          explique (Diseño, 2026-10-01, xcons-docs#458). */}
                      {/* **Sin stock.** El stock es del seller (`vendor_product`), no del
                          producto: la fila de `cataloginventory_*` la crea el alta por detrás
                          y el form no la toca. */}
                      <span id="bloque-categorias" />
                      <CategoriasPorStore
                        error={faltaCategoria}
                        seleccion={categorias}
                        nombres={nombresCategoria}
                        onChange={(ids) => {
                          setCategorias(ids);
                          tocar();
                        }}
                      />
                      {faltaCategoria && (
                        <Text type="danger" style={{ display: "block", marginTop: 8 }}>
                          Elegí al menos una categoría para enviar a revisión
                        </Text>
                      )}
                    </>
                  ),
                },
              ]}
            />
          </ConfigProvider>
        </Form>

        {/* Sin botones al pie (xcons-docs#404): Enviar a revisión va en el flujo, y Guardar
            borrador junto al título (xcons-docs#479) y en el modal de abajo, al salir con
            cambios sin guardar. */}

        {/* **El historial también mientras se edita** (xcons-docs#442), no sólo en la vista.
            Un alta todavía no tiene dónde anclarlo: se ve vacío y se llena con el primer
            guardado (xcons-docs#499). */}
        <HistorialDelProducto entityId={anclaDelHistorial} />
      </Flex>

      {/* Salir con cambios sin guardar: tres caminos, y ninguno pierde nada sin que el
          operador lo elija. */}
      <Modal
        open={saliendo}
        title={
          // El mismo ícono de alerta que usan los Modal.confirm de antd.
          <Space size={8}>
            <ExclamationCircleFilled style={{ color: "#faad14" }} />
            Tenés cambios sin guardar
          </Space>
        }
        onCancel={() => setSaliendo(false)}
        // Más ancho que los 520px de antd: con los tres botones al pie quedaba apretado.
        width={560}
        // Aire vertical entre el título, el texto y los botones: por default quedaban
        // pegados.
        styles={{ body: { padding: "24px 0 36px" } }}
        footer={
          // Salir, que es lo que pierde el trabajo, va solo a la izquierda; los dos que
          // lo conservan, a la derecha.
          <Flex justify="space-between" gap={8}>
            <Button danger onClick={salirSinGuardar}>
              Salir sin guardar
            </Button>
            <Flex gap={8}>
              <Button loading={guardando} onClick={() => void salirGuardando()}>
                {sigueBorrador ? "Guardar borrador" : "Guardar y salir"}
              </Button>
              <Button type="primary" onClick={() => setSaliendo(false)}>
                Seguir editando
              </Button>
            </Flex>
          </Flex>
        }
      >
        <Text>
          {sigueBorrador
            ? "Si salís sin guardar, no queda nada de lo que cargaste."
            : "Si salís sin guardar, se pierden los cambios que hiciste."}
        </Text>
      </Modal>

      <DuplicarProducto
        open={duplicando}
        onClose={() => setDuplicando(false)}
        sku={borrador?.sku ?? sku}
        marca={skuFabricanteGuardado ? etiquetaMarca : null}
      />

      <ModalDeRechazo
        open={rechazando}
        ejecutando={guardando}
        onRechazar={rechazar}
        onCancel={() => setRechazando(false)}
      />
    </>
  );
};

/** El árbol de un Store. 33 de los 40 comparten la root 2, así que muchos traen el mismo. */
interface ArbolDeStore {
  store: string;
  rootCategoryId: number;
  catalogMode: StoreCatalogTree["catalogMode"];
  nodes: CategoryNode[];
  /** Todos los `category_id` del árbol, para saber qué parte de la selección es suya. */
  ids: Set<number>;
}

const idsDelArbol = (nodos: CategoryNode[], out = new Set<number>()) => {
  for (const n of nodos) {
    out.add(n.categoryId);
    idsDelArbol(n.children, out);
  }
  return out;
};

/**
 * Las categorías del producto, **un bloque por Store, uno debajo del otro**, sólo de los
 * Stores donde el operador tiene el permiso `categorias` (R13). Lo pidió el PM: con un
 * select para elegir el Store no se veía de un vistazo en qué tiendas estaba el producto.
 *
 * **Uno por Store aunque compartan árbol** (Diseño, 2026-09-28 — A2.6, xcons-docs#423).
 * Antes los que comparten root iban en un solo bloque; ahora cada Store tiene el suyo.
 *
 * Cada bloque es una tarjeta con el Store de encabezado y el selector adentro, sin textos
 * de ayuda (Diseño, 2026-09-30). Lo único que avisa es el modo manual sin ninguna
 * categoría del árbol, porque ahí el producto no se muestra.
 *
 * La selección es una sola lista para todo el producto. Cada bloque muestra la parte que
 * cae en su árbol y, al cambiar, reemplaza sólo esa parte: lo que el producto tiene en
 * árboles que el operador no ve se conserva tal cual (y el backend igual acota el borrado
 * a sus tiendas).
 */
const CategoriasPorStore: React.FC<{
  seleccion: number[];
  /** Los nombres que ya resolvió el API, para las categorías fuera de los árboles visibles. */
  nombres?: Record<number, string>;
  onChange: (ids: number[]) => void;
  /** En rojo: se intentó enviar a revisión sin ninguna categoría (xcons-docs#442). */
  error?: boolean;
}> = ({ seleccion, nombres = {}, onChange, error }) => {
  const { data: identity } = useGetIdentity<any>();
  const [arboles, setArboles] = useState<ArbolDeStore[]>([]);
  const [cargando, setCargando] = useState(false);

  /** Los stores con `categorias` concedido. La forma es `permissions.store.categorias[code]`. */
  const stores = useMemo(() => storesConCategorias(identity), [identity]);

  useEffect(() => {
    if (!stores.length) return;
    let vivo = true;
    setCargando(true);
    Promise.all(
      stores.map((store) =>
        axiosInstance
          .get<StoreCatalogTree>(
            `${API_BASE_URL}/pim/stores/${encodeURIComponent(store)}/catalog/categories`,
          )
          .then(({ data }) => ({ store, data }))
          // Un store que falla no tira los demás: se omite su bloque.
          .catch(() => null),
      ),
    )
      .then((respuestas) => {
        if (!vivo) return;
        // Los ids se calculan una vez por root: 33 stores traen el mismo árbol.
        const idsPorRoot = new Map<number, Set<number>>();
        const lista: ArbolDeStore[] = [];
        for (const r of respuestas) {
          if (!r) continue;
          let ids = idsPorRoot.get(r.data.rootCategoryId);
          if (!ids) {
            ids = idsDelArbol(r.data.nodes);
            idsPorRoot.set(r.data.rootCategoryId, ids);
          }
          lista.push({
            store: r.store,
            rootCategoryId: r.data.rootCategoryId,
            catalogMode: r.data.catalogMode,
            nodes: r.data.nodes,
            ids,
          });
        }
        setArboles(lista);
      })
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [stores]);

  const aTreeData = (nodos: CategoryNode[]): any[] =>
    nodos.map((n) => ({
      value: n.categoryId,
      title: n.name,
      children: aTreeData(n.children),
    }));

  /**
   * `id → nombre`, para que los tags muestren la categoría y no su id. Con
   * `treeCheckStrictly` antd espera `{value,label}` y usa el `label` tal cual.
   */
  const nombrePorId = useMemo(() => {
    const out = new Map<number, string>();
    const recorrer = (nodos: CategoryNode[]) => {
      for (const n of nodos) {
        out.set(n.categoryId, n.name);
        recorrer(n.children);
      }
    };
    const vistos = new Set<number>();
    arboles.forEach((a) => {
      if (vistos.has(a.rootCategoryId)) return;
      vistos.add(a.rootCategoryId);
      recorrer(a.nodes);
    });
    return out;
  }, [arboles]);

  if (!stores.length) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        No tenés el permiso <Text code>Categorías</Text> en ningún Store, así que no podés
        asignarlas. El resto del producto sí se edita.
      </Text>
    );
  }

  if (cargando && !arboles.length) {
    return <Skeleton active paragraph={{ rows: 2 }} />;
  }

  return (
    <Flex vertical gap={16} style={{ width: "100%" }}>
      {arboles.map((arbol) => {
        // En automático el catálogo lo definen los sellers (R7): las categorías no deciden
        // si el producto se ve, así que ahí no hay aviso de "no se muestra".
        const automatica = arbol.catalogMode === "auto_from_sellers";
        const sinCategorias = !automatica && !seleccion.some((id) => arbol.ids.has(id));
        return (
          <Card
            key={arbol.store}
            size="small"
            title={
              <Space size={8}>
                <ShopOutlined />
                {arbol.store}
              </Space>
            }
          >
            <TreeSelect
              multiple
              treeCheckable
              /**
               * **La cascada es nuestra, no la de antd.** Marcar un padre marca su
               * descendencia y un hijo solo deja a los padres intermedios, pero sólo al
               * hacer click (`aplicarClick`): la de antd también se aplicaba al cargar, y
               * un producto con un padre solo se abría con todos sus hijos marcados.
               * `treeCheckStrictly` deja el valor en manos nuestras, al precio de que antd
               * devuelva `{value,label}` en vez del id pelado.
               */
              treeCheckStrictly
              allowClear
              showSearch
              treeNodeFilterProp="title"
              style={{ width: "100%" }}
              placeholder="Elegir categorías"
              status={error ? "error" : undefined}
              value={[
                ...seleccion
                  .filter((id) => arbol.ids.has(id))
                  .map((value) => ({
                    value,
                    label: nombrePorId.get(value) ?? nombres[value] ?? `#${value}`,
                  })),
                // Los padres con algún hijo elegido: se ven intermedios y no se asignan.
                ...intermedias(
                  seleccion.filter((id) => arbol.ids.has(id)),
                  arbol.nodes,
                ).map((value) => ({
                  value,
                  label: nombrePorId.get(value) ?? `#${value}`,
                  halfChecked: true,
                })),
              ]}
              onChange={(v, _labels, extra) => {
                const delArbol = seleccion.filter((id) => arbol.ids.has(id));
                const clickeada = Number(
                  (extra?.triggerValue as any)?.value ?? extra?.triggerValue,
                );
                // Un click en el árbol aplica la cascada, y sacar un tag también: trae
                // `checked: false` igual que desmarcarlo, así que se lleva a sus hijos.
                // Vaciar con la cruz no trae `triggerValue`: ahí vale lo que quedó, sin
                // los intermedios.
                const elegidas =
                  Number.isFinite(clickeada) && typeof extra?.checked === "boolean"
                    ? aplicarClick(delArbol, arbol.nodes, clickeada, extra.checked)
                    : (Array.isArray(v) ? v : [])
                        .filter((x: any) => !x?.halfChecked)
                        .map((x: any) => Number(x?.value ?? x))
                        .filter((n) => Number.isFinite(n));
                // Se reemplaza sólo la parte de ESTE árbol; el resto de la selección
                // queda.
                onChange([
                  ...seleccion.filter((id) => !arbol.ids.has(id)),
                  ...elegidas,
                ]);
              }}
              treeData={aTreeData(arbol.nodes)}
              maxTagCount={6}
              /**
               * **El primer click en el árbol se perdía.** Al apretar el mouse en el
               * desplegable, rc-select le devuelve el foco al input con `focus()`, y si el
               * input estaba fuera de pantalla el navegador scrolleaba para mostrarlo: el
               * desplegable se movía y el click se soltaba en otro lado. Cortar el mousedown
               * acá deja la página quieta; el input no pierde el foco porque la lista ya
               * hace `preventDefault` en el mismo evento.
               */
              dropdownRender={(lista) => (
                <div onMouseDown={(e) => e.stopPropagation()}>{lista}</div>
              )}
            />
            {sinCategorias && (
              <Text
                type="warning"
                style={{ fontSize: 12, display: "block", marginTop: 4 }}
              >
                No se muestra en esta tienda: el producto no está en ninguna de sus
                categorías.
              </Text>
            )}
          </Card>
        );
      })}
    </Flex>
  );
};
